import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { servicesConfig, ServiceConfig } from "../../config/serviceDashboardConfig";
import "./ServiceOverview.css";
import Headline from "../../components/Headline/Headline";
import { useService } from "../../context/ServiceProvider";

const getStatusText = (status?: string) => {
  switch (status) {
    case "green": return "Online";
    case "yellow": return "Teilweise verfügbar";
    case "red": return "Offline";
    default: return "Unbekannt";
  }
};

const getGlobalStatus = (statusMap: Record<string, string>, total: number) => {
  const statuses = Object.values(statusMap);
  if (statuses.length === 0) return "unknown";
  if (statuses.every((s) => s === "green")) return "all-ok";
  if (statuses.every((s) => s === "red")) return "all-down";
  return "issues";
};

const ServiceCard = ({
  service,
  status,
  pulseTrigger,
  onClick,
}: {
  service: ServiceConfig;
  status?: string;
  pulseTrigger: boolean;
  onClick: () => void;
}) => (
  <div className="service-card enhanced" onClick={onClick}>
    <div className="service-header">
      <div className={`status-dot ${status || "yellow"} ${pulseTrigger ? "pulse" : ""}`} />
      <h4>{service.displayName}</h4>
    </div>
    <p className="status-text">Status: <strong>{getStatusText(status)}</strong></p>
    <p className="api-url">{service.apiBaseUrl || "Keine API konfiguriert"}</p>
  </div>
);

const ServiceOverview = () => {
  const [statusMap, setStatusMap] = useState<Record<string, "green" | "yellow" | "red">>({});
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);
  const [pulseTrigger, setPulseTrigger] = useState(false);
  const { setCurrentService} = useService();
  const navigate = useNavigate();

  const services = Object.values(servicesConfig);
  const globalStatus = getGlobalStatus(statusMap, services.length);

  const checkHealthStatus = () => {
    services.forEach((service) => {
      if (!service.apiBaseUrl) {
        setStatusMap((prev) => ({ ...prev, [service.id]: "red" }));
        return;
      }

      fetch(`${service.apiBaseUrl}/HealthCheck`)
        .then((res) => {
          const status = res.ok ? "green" : "yellow";
          setStatusMap((prev) => ({ ...prev, [service.id]: status }));
        })
        .catch(() => {
          setStatusMap((prev) => ({ ...prev, [service.id]: "red" }));
        });
    });

    setLastUpdated(new Date().toLocaleTimeString());
    setPulseTrigger(true);
    setTimeout(() => setPulseTrigger(false), 1500);
  };

  useEffect(() => {
    checkHealthStatus();
    const interval = setInterval(checkHealthStatus, 15000);
    return () => clearInterval(interval);
  }, []);

  const handleServiceClick = (service: ServiceConfig) => {
    setCurrentService(service);
    navigate(`/${service.id}/dashboard`);
  };

  return (
    <div className="service-overview-container">
      <Headline title="Service Übersicht" subtitle="Wähle einen Service aus, um das Dashboard anzuzeigen" />

      <div className={`global-status-banner ${globalStatus}`}>
        <div className="status-content">
          <div className="status-icon">
            {globalStatus === "all-ok" && "✅"}
            {globalStatus === "issues" && "⚠️"}
            {globalStatus === "all-down" && "❌"}
            {globalStatus === "unknown" && "⏳"}
          </div>
          <div className="status-text">
            <h3>
              {globalStatus === "all-ok" && "Alle Services laufen"}
              {globalStatus === "issues" && "Einige Services melden Probleme"}
              {globalStatus === "all-down" && "Alle Services sind derzeit offline"}
              {globalStatus === "unknown" && "Status wird geladen..."}
            </h3>
            <p>
              {Object.values(statusMap).filter((s) => s === "green").length} von {services.length} Services online
              {lastUpdated && ` • Letztes Update: ${lastUpdated}`}
            </p>
          </div>
        </div>
      </div>

      <div className="service-list">
        {services.map((service) => (
          <ServiceCard
            key={service.id}
            service={service}
            status={statusMap[service.id]}
            pulseTrigger={pulseTrigger}
            onClick={() => handleServiceClick(service)}
          />
        ))}
      </div>
    </div>
  );
};

export default ServiceOverview;
